import request from '@/utils/request'
import { v2Host } from '@/config/host'
import type {
  EventSummary,
  TimeRange,
  EventTrendModel,
  GetSceneDetailParams,
  GetEventTrendDataParams,
  SceneDetailItem,
  EventListQuery,
  ProjectListOptionsQuery,
  ProjectListOption,
  EventListItem,
} from './index.type'
import type { _Response } from '../type'
import { AxiosRequestConfig } from 'axios'
import { cookRequestParams } from '@/common/request'
import { TrafficType } from '@pt/enums/insights'
import { cookMetricPrefix } from '@pt/views/dashboard/utils'
import _ from 'lodash'
import type { ListData } from 'request'

export const resolveData = (data: Record<string, any>) => {
  const params = {} as any
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const element = data[key]
      if (Array.isArray(element)) {
        params[key] = element.toString()
      } else {
        params[key] = element
      }
    }
  }

  return params
}

// 事件分析概览
export function getEventSummary(
  data: TimeRange & {
    traffic_types?: TrafficType[]
  },
): _Response<EventSummary> {
  const params = resolveData(data)

  return request({
    url: `${v2Host}/insights/event/summary`,
    method: 'get',
    params: params,
  })
}

// 事件图表分析
export function getEventTrendData(
  _data: GetEventTrendDataParams,
  options: AxiosRequestConfig = {},
): _Response<EventTrendModel> {
  return request({
    url: `${v2Host}/insights/event/graph`,
    method: 'get',
    params: cookRequestParams({
      ..._data,
      metrics: cookMetricPrefix({
        rule: 'remove',
        value: _data.metrics,
      }),
    }),
    ...options,
  }).then((res) => {
    // eslint-disable-next-line no-undef
    const _res = res as unknown as Awaited<_Response<EventTrendModel>>
    _res.data.data.metrics?.forEach((item) => {
      const temp = cookMetricPrefix({ rule: 'add', value: item.name })
      if (temp !== item.name) {
        item.name = temp
      }
    })
    return _res
  })
}

// 场景分析报表
// 场景分析报表
export function getSceneDetail(
  data: GetSceneDetailParams,
  options: AxiosRequestConfig = {},
): _Response<{ list: null | SceneDetailItem[] }> {
  return request({
    url: `${v2Host}/insights/scene/detail`,
    method: 'get',
    params: {
      ...data,
      ad_types: data.ad_types?.toString(),
      breakdowns: data.breakdowns.toString(),
      metrics: cookMetricPrefix({
        rule: 'remove',
        value: data.metrics,
      }).toString(),
      country_codes: data.country_codes?.toString(),
      scenes: data.scenes?.toString(),
    },
    ...options,
  }).then((res) => {
    // 表格中使用props交互指标增加了p_前缀
    const list = _.get(res, 'data.data.list') || []
    if (list.length) {
      res.data.data.list = list.map((item) => {
        for (const key in item) {
          const temp = cookMetricPrefix({ rule: 'add', value: key })
          if (temp !== key) {
            item[temp] = item[key]
          }
        }
        return item
      })
    }
    // res.data.data.list = []
    // add(list, {
    //   rule: '1.1.1',
    // })

    return res
  })
}
/**
 * 事件详情报表
 * @param data ListQuery
 * @returns EventListItem[]
 */
export function getEventList(
  data: EventListQuery,
  options: AxiosRequestConfig = {},
): _Response<ListData<EventListItem>> {
  return request({
    url: `${v2Host}/insights/event/detail`,
    method: 'get',
    params: cookRequestParams({
      ...data,
      metrics: cookMetricPrefix({
        rule: 'remove',
        value: data.metrics,
      }),
    }),
    ...options,
  }).then((res) => {
    // eslint-disable-next-line no-undef
    const _res = res as unknown as Awaited<_Response<ListData<EventListItem>>>
    _res.data.data.list?.forEach((item: any) => {
      for (const key in item) {
        const temp = cookMetricPrefix({ rule: 'add', value: key })
        if (temp !== key) {
          item[temp] = item[key]
        }
      }
    })

    return _res
  })
}

// 场景分析报表导出
export function exportSceneDetail(
  data: GetSceneDetailParams,
  options: AxiosRequestConfig = {},
): _Response<{ data: number }> {
  return request({
    url: `${v2Host}/insights/scene/export`,
    method: 'get',
    params: {
      ...data,
      ad_types: data.ad_types?.toString(),
      breakdowns: data.breakdowns.toString(),
      metrics: cookMetricPrefix({
        rule: 'remove',
        value: data.metrics,
      }).toString(),
      country_codes: data.country_codes?.toString(),
      scenes: data.scenes?.toString(),
    },
    ...options,
  })
}

// 事件报表导出
export function exportEventList(
  data: EventListQuery,
  options: AxiosRequestConfig = {},
): _Response<{ data: number }> {
  return request({
    url: `${v2Host}/insights/event/export`,
    method: 'get',
    params: cookRequestParams({
      ...data,
      metrics: cookMetricPrefix({
        rule: 'remove',
        value: data.metrics,
      }),
    }),
    ...options,
  })
}

// 支持的选项类型（option_type）：
// app 投放产品
// campaign 广告系列
// adset 广告计划
// material_name 素材/创意名称
// template 可玩模版
// project 项目
// ad_type 广告类型
export type OptionsType =
  | 'ad'
  | 'app'
  | 'adset'
  | 'template'
  | 'project'
  | 'material_name'
  | 'creative_name'
  | 'mws_creative_name'
  | 'campaign'
  | 'offer'
  | 'ad_type'
  | 'ad_type2'
  | 'basic_id'
  | 'fingerprint'
  | 'mid'

export function getOptions(
  data: {
    keyword: string
    option_type: OptionsType
  },
  options: AxiosRequestConfig = {},
): _Response<{ options: null | ProjectListOption[] }> {
  return request({
    url: `${v2Host}/insights/options/list`,
    method: 'get',
    params: data,
    ...options,
  })
}

export function fetchProjectListOptions(
  params: ProjectListOptionsQuery,
  options: AxiosRequestConfig = {},
): _Response<{ list: null | any[] }> {
  return request({
    url: `${v2Host}/insights/options/project-list`,
    method: 'get',
    params,
    ...options,
  })
}

export function fetchProductListOptions(
  params: TimeRange,
  options: AxiosRequestConfig = {},
): _Response<{ list: null | { app_name: string; package_name: string }[] }> {
  return request({
    url: `${v2Host}/insights/options/product-list`,
    method: 'get',
    params,
    ...options,
  })
}
